import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService, IWebAppUser } from '@services/authentication.service'
import { AccountsService, IAccount } from '@services/accounts.service';
import { GlobalFunctions, imagePath, imgTypes } from '@global/GlobalFunctions';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { combineLatest, firstValueFrom, Subject } from 'rxjs';
import { takeUntil, distinctUntilChanged, filter } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { EditAccountComponent } from 'src/app/dialogs/edit-account/edit-account.component';

/**
 * AccountManageComponent
 */
@Component({
	selector: 'app-account-manage',
	templateUrl: './account-manage.component.html',
	styleUrls: ['./account-manage.component.scss'],
})
export class AccountManageComponent implements OnInit, OnDestroy {

	@Output() accountsListUpdated = new EventEmitter<boolean>();

	/** selected Tab Index */
	public selectedTabIndex: number;

	/** Route Parameter Account ID */
	public paramAccountID: string = "";

	/** My Accounts List */
	public myAccounts: IAccount[];

	/** Selected Account */
	public selectedAccount: IAccount;

	/** Current User */
	public currentUser: IWebAppUser;

	/** Account GUID */
	public accountGUID: string;

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** Selected Tab Index */
	public selectedTab: number = 0;

	/** List of Tabs */
	public lstTabs = [
		{
			title: 'Users',
			name: 'users',
			icon: 'people'
		},
		{
			title: 'Groups',
			name: 'groups',
			icon: 'groups'
		},
		{
			title: 'Sites',
			name: 'sites',
			icon: 'place'
		},
		{
			title: 'Account Features',
			name: 'features',
			icon: 'extension',
			adminOnly: true
		}
	];

	/**
	 * Default Constructor
	 * @param activatedRoute activatedRoute
	 * @param authenticationService authenticationService
	 * @param globalFunctions globalFunctions
	 * @param siteSubscriptions siteSubscriptions
	 * @param dialog dialog
	 * @param accountsService accountsService
	 */
	constructor(
		private activatedRoute: ActivatedRoute,
		private authenticationService: AuthenticationService,
		private globalFunctions: GlobalFunctions,
		private siteSubscriptions: siteSubscriptions,
		public dialog: MatDialog,
		private accountsService: AccountsService,
		private router: Router,
		private route: ActivatedRoute
	) { }

	/**
	 * tabChange
	 * @param event event  
	 */
	tabChange(event: any) {
		this.selectedTab = event.selectedIndex;
		let tabName: string = this.lstTabs[this.selectedTab].name;
		this.router.navigate([], {
			relativeTo: this.route,
			queryParams: { selectedTab: tabName },
			queryParamsHandling: 'merge'
		});
	}

	/** Path to Accounts Images */
	public imagePathAccounts(accountGUID?: string): string {
		return imagePath(accountGUID, imgTypes.accounts).toLowerCase()
	}

	/**
	 * ngOnInit
	 * @param event 
	 */
	onImageError(event: Event): void {
		(event.target as HTMLImageElement).src =
			'https://stmgwebappstorage.blob.core.windows.net/prismpdm/assets/noimage.png';
	}

	/**
	 * Patch the selected Account
	 * @param account
	 */
	editAccount(account: IAccount) {

		// Create a 'Local'/shallow copy of the Asset Information
		var _edit = { ...account }

		// Display the dialog box
		const dialogRef = this.dialog.open(EditAccountComponent, {
			data: _edit,
		});

		// Update the original Asset with the result
		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(async result => {

			_edit = await this.patchAccount(result);
			this.refreshAccountsList();
		});
	}

	/**
	 * Patch the selected Account
	 * @param _edit
	 */
	async patchAccount(_edit: IAccount, assignResult: boolean = false) {
		try {
			this.globalFunctions.displaySpinner(true)
			const response = await firstValueFrom(
				this.accountsService.patchAccount$(_edit.GUID, this.currentUser.Sub, _edit)
			);
			let showDisabled = false
			this.globalFunctions.DisplaySnackBar({ message: "Account Details Updated", duration: 1000 });

			if (assignResult) {
				Object.assign(this.selectedAccount, response);
				this.siteSubscriptions.SetSelectedAccount(this.selectedAccount, true);
			}
			return response;

		} catch (error: any) {
			if (error?.status === 404) {
				this.globalFunctions.HandleError(error, "patchAccount: Error updating Accounts");
			} else {
				this.globalFunctions.HandleError(error, "");
			}
			return null;
		} finally { this.globalFunctions.displaySpinner(false); }

	}

	/**
 * Refresh My Accounts Subscription
 */
	refreshAccountsList() {
		let data = {
			"userPublicID": this.currentUser.Sub,
			"showDeleted": true
		}
		this.siteSubscriptions.RefreshMyAccounts(data)
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {

		combineLatest([
			this.authenticationService.currentUser$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.activatedRoute.paramMap.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.route.queryParamMap.pipe(distinctUntilChanged()),
			this.siteSubscriptions.selectedAccount$.pipe(distinctUntilChanged()),
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([currentUser, paramMap, queryParamMap, selectedAccount]) =>
				!!currentUser && !!paramMap.has('accountID') && !!queryParamMap && !!selectedAccount)
		).subscribe({
			next: ([currentUser, paramMap, queryParamMap, selectedAccount]) => {

				// Check we have a validated User
				if (currentUser && !this.currentUser) {
					this.currentUser = <IWebAppUser>currentUser
				}
				
				// Get the GUID for the selected Account to Manage
				this.accountGUID = paramMap.get('accountID') ?? ""

				var savedTab = queryParamMap.get('selectedTab') ?? ""
				this.selectedTab = this.lstTabs.findIndex(tab => tab.name === savedTab);
				this.selectedAccount = selectedAccount;

			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: this.authenticationService.currentUser$", false) },
		})
	}

	/**
	 * View the Image Fullsize
	 * @param imageURL
	 */
	viewImage(imageURL: string) {
		this.globalFunctions.viewImageFullSize(imageURL);
	}

}
